import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Grid, FormControl, LinearProgress,
} from '@material-ui/core';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import i18n from '@i18n';
import { useAnalisis } from '@hooks';
import {
  Alert, ButtonAdorned, MultiFileUpload, Stepper, Divider, DatePicker,
} from '@components/atoms';
import constants from '@helpers/constants';
import { AnalisisDetails, InformationBox } from '@components/molecules';
import { createRedaccionAnalisisSchema } from '@helpers/schemas';
import AuthContext from '@contexts/AuthContext';
import moment from 'moment';

const Redaccion = ({ data }) => {
  const { pages: { employees: { analisis: { redaccion: texts } } } } = i18n;
  const history = useHistory();
  const {
    editAnalisis, isAnalisisLoading, analisisError, getFiles, archiveAnalisis,
  } = useAnalisis();

  const [analisis, setAnalisis] = useState({
    customer: '',
    presupuesto: {},
    docsResultado: [],
    docsPlantilla: [],
    docsRedaccion: [],
    number: '',
    members: [],
  });
  const { id, sample, area } = data;
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useContext(AuthContext);
  const [showButtonContinue, setShowButtonContinue] = useState(false);
  const [showButtonArchive, setShowButtonArchive] = useState(false);
  const stepRedaccion = user?.rolAnalisis ? !user.rolAnalisis.steps.filter((step) => step.id === 'redaccion')[0].enabled : false;

  const form = useFormik({
    initialValues: analisis,
    enableReinitialize: true,
    validationSchema: createRedaccionAnalisisSchema,
    validateOnChange: true,
    onSubmit: async (values) => {
      const { docsRedaccion, customer } = values;
      const updated = await editAnalisis({
        id,
        files: docsRedaccion,
        filesType: 'redaccion',
        step: 'completado',
        customer,
        sample,
        area,
      });

      if (updated) {
        enqueueSnackbar(texts.success, { variant: 'success' });
        history.push('/e/analisis');
      }
    },
  });

  const handleAgain = async () => {
    const updated = await editAnalisis({
      id,
      step: 'revision',
      sample,
      area,
    });

    if (updated) {
      enqueueSnackbar(texts.success, { variant: 'success' });
      history.push('/e/analisis');
    }
  };

  useEffect(async () => {
    const docsPlantilla = await getFiles({ customerId: data.customer.id, analisisId: id, filesType: 'plantilla' });
    const docsIngreso = await getFiles({ customerId: data.customer.id, analisisId: id, filesType: 'ingreso' });
    const docsRedaccion = await getFiles({ customerId: data.customer.id, analisisId: id, filesType: 'redaccion' });
    const docsResultado = await getFiles({ customerId: data.customer.id, analisisId: id, filesType: 'resultado' });
    const anexos = await getFiles({ customerId: data.customer.id, analisisId: id, filesType: 'anexos' });

    setAnalisis({
      ...data,
      docsPlantilla,
      docsResultado,
      docsIngreso,
      anexos: anexos || [],
      docsRedaccion: docsRedaccion || [],
      executeDate: data.executeDate ? moment(data.executeDate.toString().replace('Z', '')).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
      limitDate: data.limitDate ? moment(data.limitDate.toString().replace('Z', '')).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
    });

    const isAdministrador = user.roles.find((rol) => (rol.id === constants.ROLES.ADMINISTRADOR));
    const isJefeLaboratorio = user.roles.find((rol) => (rol.id === constants.ROLES.JEFE_LABORATORIO));
    const isSecretaria = user.roles.find((rol) => (rol.id === constants.ROLES.SECRETARIA));
    const isIngeniero = user.roles.find((rol) => (rol.id === constants.ROLES.INGENIERO));
    const isAsistente = user.roles.find((rol) => (rol.id === constants.ROLES.ASISTENTE_GERENCIA));

    setShowButtonContinue(isAdministrador || isJefeLaboratorio || isIngeniero || isAsistente);
    setShowButtonArchive(isAdministrador || isSecretaria);
  }, []);

  const handleArchived = async () => {
    const response = await archiveAnalisis(analisis.id);
    if (response) {
      enqueueSnackbar(texts.archived, { variant: 'warning' });
      history.push('/e/analisis');
    }
  };

  return (
    <>
      {analisisError && <Alert>{analisisError.message}</Alert>}
      { (isAnalisisLoading || analisis.docsResultado.length === 0) ? <LinearProgress /> : (
        <>
          <Stepper activeStep="redaccion" steps={constants.STEPS_ANALISIS} />
          <AnalisisDetails analisis={analisis} />
          <Grid container spacing={2} style={{ marginTop: '0.8rem' }}>
            <Grid item xs={12} sm={3} md={3}>
              <DatePicker
                label={texts.limitDate}
                value={analisis.limitDate}
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={3} md={3}>
              <DatePicker
                label={texts.executeDate}
                value={analisis.executeDate}
                disabled
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <MultiFileUpload
                  title={texts.anexos}
                  name="anexos"
                  onChange={form.handleChange}
                  value={form.values.anexos}
                  error={form.touched.anexos && Boolean(form.errors.anexos)}
                  helperText={form.touched.anexos && form.errors.anexos && form.errors.anexos}
                  multiple
                  // disabled
                  urls={analisis.anexos}
                  // accepted={['images']}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <MultiFileUpload
                  title={texts.docsResultado}
                  urls={analisis.docsResultado}
                  disabled
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <InformationBox
                updatedAt={analisis.docsResultado && analisis.docsResultado.length !== 0 && analisis.docsResultado[0].timeCreated}
              />
            </Grid>
          </Grid>
          <Divider />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <MultiFileUpload
                  title={texts.docsRedaccion}
                  name="docsRedaccion"
                  onChange={form.handleChange}
                  value={form.values.docsRedaccion}
                  error={form.touched.docsRedaccion && Boolean(form.errors.docsRedaccion)}
                  helperText={form.touched.docsRedaccion && form.errors.docsRedaccion && form.errors.docsRedaccion}
                  multiple
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid container spacing={1} justifyContent="space-between" direction="row-reverse" alignItems="center" style={{ marginTop: '20px' }}>
            <Grid item>
              <Grid container spacing={1} justifyContent="flex-end">
                <Grid item>
                  <ButtonAdorned color="secondary" onClick={handleAgain}>
                    {texts.again}
                  </ButtonAdorned>
                </Grid>
                <Grid item>
                  <ButtonAdorned color="default" onClick={() => history.push('/e/analisis')}>
                    {texts.list}
                  </ButtonAdorned>
                </Grid>
                {showButtonContinue && (
                <Grid item>
                  <form onSubmit={form.handleSubmit}>
                    <ButtonAdorned loading={isAnalisisLoading} disabled={isAnalisisLoading || stepRedaccion} type="submit">
                      {texts.save}
                    </ButtonAdorned>
                  </form>
                </Grid>
                )}
              </Grid>
            </Grid>
            {showButtonArchive
              && (
              <Grid item>
                <ButtonAdorned color="secondary" onClick={handleArchived}>
                  {texts.archive}
                </ButtonAdorned>
              </Grid>
              )}
          </Grid>
        </>
      )}
    </>
  );
};

Redaccion.propTypes = {
  id: PropTypes.string,
  data: PropTypes.shape(),
};

Redaccion.defaultProps = {
  id: '',
  data: {},
};

export default Redaccion;
